import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

// Particle component using framer-motion
const Particle = ({ size, color, delay, duration }) => {
  const randomX = Math.random() * 100;
  const randomY = Math.random() * 100;

  // Framer motion animation variants
  const floatVariants = {
    initial: {
      opacity: 0,
      scale: 0
    },
    animate: {
      opacity: 0.7,
      scale: 1,
      y: [0, -20, 0],
      x: [0, 10, 0],
      transition: {
        y: {
          repeat: Infinity,
          duration: duration,
          ease: 'easeInOut',
          delay
        },
        x: {
          repeat: Infinity,
          duration: duration * 1.2,
          ease: 'easeInOut',
          delay
        },
        opacity: {
          duration: 1,
          delay: delay * 0.5
        },
        scale: {
          duration: 1.5,
          delay: delay * 0.5
        }
      }
    }
  };

  return (
    <motion.div
      style={{
        position: 'absolute',
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: '50%',
        top: `${randomY}%`,
        left: `${randomX}%`,
        boxShadow: `0 0 ${size / 3}px ${color}`
      }}
      variants={floatVariants}
      initial="initial"
      animate="animate"
    />
  );
};

// Cube element using framer-motion
const CubeElement = ({ size, color, delay, duration }) => {
  const randomX = Math.random() * 100;
  const randomY = Math.random() * 100;

  // Framer motion animation variants
  const cubeVariants = {
    initial: {
      opacity: 0,
      scale: 0
    },
    animate: {
      opacity: 0.8,
      scale: 1,
      rotate: 360,
      y: [0, -30, 0],
      x: [0, 15, 0],
      transition: {
        rotate: {
          repeat: Infinity,
          duration: duration,
          ease: 'linear'
        },
        y: {
          repeat: Infinity,
          duration: duration * 1.5,
          ease: 'easeInOut',
          delay
        },
        x: {
          repeat: Infinity,
          duration: duration * 1.3,
          ease: 'easeInOut',
          delay
        },
        opacity: {
          duration: 1.5,
          delay: delay * 0.5
        },
        scale: {
          duration: 2,
          delay: delay * 0.5
        }
      }
    }
  };

  return (
    <motion.div
      style={{
        position: 'absolute',
        width: size,
        height: size,
        border: `2px solid ${color}`,
        top: `${randomY}%`,
        left: `${randomX}%`
      }}
      variants={cubeVariants}
      initial="initial"
      animate="animate"
    />
  );
};

// Add a new geometric element - triangles
const Triangle = ({ size, color, delay, duration }) => {
  const randomX = Math.random() * 100;
  const randomY = Math.random() * 100;

  const triangleVariants = {
    initial: {
      opacity: 0,
      scale: 0
    },
    animate: {
      opacity: 0.7,
      scale: 1,
      rotate: [-10, 20, -10],
      y: [0, -15, 0],
      x: [0, 8, 0],
      transition: {
        rotate: {
          repeat: Infinity,
          duration: duration * 1.2,
          ease: 'easeInOut'
        },
        y: {
          repeat: Infinity,
          duration: duration,
          ease: 'easeInOut',
          delay
        },
        x: {
          repeat: Infinity,
          duration: duration * 1.1,
          ease: 'easeInOut',
          delay
        },
        opacity: {
          duration: 1.2,
          delay: delay * 0.5
        },
        scale: {
          duration: 1.8,
          delay: delay * 0.5
        }
      }
    }
  };

  // Create a triangle using borders
  return (
    <motion.div
      style={{
        position: 'absolute',
        width: 0,
        height: 0,
        borderLeft: `${size / 2}px solid transparent`,
        borderRight: `${size / 2}px solid transparent`,
        borderBottom: `${size}px solid ${color}`,
        top: `${randomY}%`,
        left: `${randomX}%`
      }}
      variants={triangleVariants}
      initial="initial"
      animate="animate"
    />
  );
};

// Enhanced Comet component with longer trail, better fade and color variations
const Comet = ({ id, delay, onComplete, color = '#ffffff' }) => {
  // Direction and position calculation remains the same
  const direction = Math.floor(Math.random() * 4);

  let startX;
  let startY;
  let endX;
  let endY;

  switch (direction) {
    case 0: // Left to right
      startX = -5;
      startY = 30 + Math.random() * 40;
      endX = 105;
      endY = startY + (Math.random() * 40 - 20);
      break;
    case 1: // Right to left
      startX = 105;
      startY = 30 + Math.random() * 40;
      endX = -5;
      endY = startY + (Math.random() * 40 - 20);
      break;
    case 2: // Top to bottom
      startX = 30 + Math.random() * 40;
      startY = -5;
      endX = startX + (Math.random() * 40 - 20);
      endY = 105;
      break;
    default: // Bottom to top
      startX = 30 + Math.random() * 40;
      startY = 105;
      endX = startX + (Math.random() * 40 - 20);
      endY = -5;
      break;
  }

  // Calculate angle for tail orientation
  const angle =
    Math.atan2(endY - startY, endX - startX) * (180 / Math.PI) + 180;

  return (
    <motion.div
      style={{
        position: 'absolute',
        left: `${startX}%`,
        top: `${startY}%`,
        width: '8px',
        height: '8px',
        zIndex: 5
      }}
      animate={{
        left: `${endX}%`,
        top: `${endY}%`,
        transition: {
          duration: 3,
          ease: 'linear',
          delay
        }
      }}
      onAnimationComplete={() => onComplete(id)}
    >
      {/* Comet head */}
      <div
        style={{
          position: 'absolute',
          width: '8px',
          height: '8px',
          backgroundColor: color,
          borderRadius: '50%',
          boxShadow: `0 0 30px 8px ${color}CC`
        }}
      />

      {/* Comet tail - longer with enhanced fade in correct direction */}
      <div
        style={{
          position: 'absolute',
          left: '4px',
          top: '4px',
          width: '180px', // Longer trail
          height: '3px',
          background: `linear-gradient(to right, ${color}FF, ${color}EE 30%, ${color}99 60%, ${color}10 85%, ${color}00)`,
          transformOrigin: '0 50%',
          transform: `rotate(${angle}deg)`,
          boxShadow: `0 0 8px 1px ${color}80`
        }}
      />
    </motion.div>
  );
};

const CssBackground = () => {
  const [elements, setElements] = useState([]);
  const [comets, setComets] = useState([]);
  const intervalRef = useRef(null);

  // Function to remove a comet by its ID
  const removeComet = (id) => {
    setComets((prevComets) =>
      prevComets.filter((comet) => comet.key !== `comet-${id}`)
    );
  };

  // Updated spawn comet function with color variations
  const spawnComet = () => {
    const cometId = Date.now();

    // Comet color variations
    const cometColors = [
      '#ffffff', // White
      '#76fffc', // Cyan
      '#76b5ff', // Light blue
      '#c576ff', // Purple
      '#ffde76', // Gold
      '#ff76b5' // Pink
    ];

    const randomColor =
      cometColors[Math.floor(Math.random() * cometColors.length)];

    const newComet = (
      <Comet
        key={`comet-${cometId}`}
        id={cometId}
        delay={0}
        onComplete={removeComet}
        color={randomColor}
      />
    );

    setComets((prevComets) => [...prevComets, newComet]);
  };

  // Handle visibility change to prevent comet pileup
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // Page is now visible, clear accumulated comets
      setComets([]);

      // Restart comet generation
      clearInterval(intervalRef.current);
      spawnComet(); // Initial comet

      intervalRef.current = setInterval(() => {
        if (Math.random() < 0.5) {
          spawnComet();
        }
      }, 2000);
    } else {
      // Page is hidden, clear the interval to stop creating new comets
      clearInterval(intervalRef.current);
    }
  };

  useEffect(() => {
    // Generate elements when component mounts
    const generateElements = () => {
      const newElements = [];
      const colors = [
        '#6b76ff',
        '#76b5c5',
        '#7e76c5',
        '#c576b5',
        '#ff7676',
        '#76ffb5'
      ];

      // Generate particles
      for (let i = 0; i < 20; i++) {
        const size = Math.random() * 10 + 5;
        const delay = Math.random() * 2;
        const duration = Math.random() * 5 + 5;
        const color = colors[Math.floor(Math.random() * colors.length)];

        newElements.push(
          <Particle
            key={`particle-${i}`}
            size={size}
            color={color}
            delay={delay}
            duration={duration}
          />
        );
      }

      // Generate cubes
      for (let i = 0; i < 10; i++) {
        const size = Math.random() * 30 + 20;
        const delay = Math.random() * 2;
        const duration = Math.random() * 8 + 8;
        const color = colors[Math.floor(Math.random() * colors.length)];

        newElements.push(
          <CubeElement
            key={`cube-${i}`}
            size={size}
            color={color}
            delay={delay}
            duration={duration}
          />
        );
      }

      // Generate triangles
      for (let i = 0; i < 8; i++) {
        const size = Math.random() * 25 + 15;
        const delay = Math.random() * 2;
        const duration = Math.random() * 6 + 6;
        const color = colors[Math.floor(Math.random() * colors.length)];

        newElements.push(
          <Triangle
            key={`triangle-${i}`}
            size={size}
            color={color}
            delay={delay}
            duration={duration}
          />
        );
      }

      setElements(newElements);
    };

    generateElements();

    // Set up visibility change detection
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial comet generation
    spawnComet();

    intervalRef.current = setInterval(() => {
      if (Math.random() < 0.5) {
        spawnComet();
      }
    }, 2000);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <motion.div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        zIndex: -1
      }}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: { duration: 1 }
      }}
    >
      {comets}
      {elements}
    </motion.div>
  );
};

export default CssBackground;
